/**
 * First-party web analytics client. Captures pageviews, clicks and business
 * events and POSTs them to /api/public/analytics/event.
 *
 * Design:
 * - Visitor id: persistent cookie (1y), survives across sessions
 * - Session id: cookie with 30min sliding expiration
 * - UTM parameters: parsed from the first hit and stored in sessionStorage
 * - Uses `sendBeacon` when available so unload-time events are reliable
 * - No blocking: all fire-and-forget, any failure is logged and swallowed
 */

const API_BASE = import.meta.env.VITE_API_URL ?? "";
const ENDPOINT = `${API_BASE}/api/public/analytics/event`;

const VISITOR_COOKIE = "bzv_visitor";
const SESSION_COOKIE = "bzv_session";
const UTM_STORAGE_KEY = "bzv_utm";
const CONSENT_STORAGE_KEY = "bzv_cookie_consent";
const SESSION_TIMEOUT_MINUTES = 30;

/**
 * Exposed pra quem precisar saber se o visitante aceitou cookies (ex: pra
 * pedir permissão de algo extra). Tracking principal NÃO depende disso —
 * o banner é informativo e a captura é best-effort antes do consent.
 */
export function hasConsent(): boolean {
  try {
    return localStorage.getItem(CONSENT_STORAGE_KEY) === "accepted";
  } catch {
    return false;
  }
}

// ── Cookies ─────────────────────────────────────────────────────────────

function setCookie(name: string, value: string, maxAgeSeconds: number) {
  try {
    document.cookie = `${name}=${value}; max-age=${maxAgeSeconds}; path=/; SameSite=Lax`;
  } catch {
    // noop
  }
}

function getCookie(name: string): string | null {
  try {
    const match = document.cookie.match(new RegExp("(^|; )" + name + "=([^;]*)"));
    return match ? decodeURIComponent(match[2]) : null;
  } catch {
    return null;
  }
}

function randomId(prefix: string): string {
  const arr = new Uint8Array(16);
  try {
    crypto.getRandomValues(arr);
  } catch {
    for (let i = 0; i < 16; i++) arr[i] = Math.floor(Math.random() * 256);
  }
  return (
    prefix +
    "_" +
    Array.from(arr)
      .map((b) => b.toString(16).padStart(2, "0"))
      .join("")
  );
}

function getOrCreateVisitorKey(): string {
  let key = getCookie(VISITOR_COOKIE);
  if (!key) {
    key = randomId("vis");
    setCookie(VISITOR_COOKIE, key, 365 * 24 * 60 * 60); // 1 year
  }
  return key;
}

function getOrCreateSessionKey(): string {
  let key = getCookie(SESSION_COOKIE);
  if (!key) {
    key = randomId("sess");
  }
  // Refresh the sliding expiration on every event
  setCookie(SESSION_COOKIE, key, SESSION_TIMEOUT_MINUTES * 60);
  return key;
}

// ── UTM / landing capture ──────────────────────────────────────────────

interface UtmBundle {
  source?: string;
  medium?: string;
  campaign?: string;
  term?: string;
  content?: string;
  landingPath?: string;
  referer?: string;
}

function captureOrLoadUtm(): UtmBundle {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    if (stored) return JSON.parse(stored) as UtmBundle;
  } catch {
    // ignore
  }
  const params = new URLSearchParams(window.location.search);
  const utm: UtmBundle = {
    source: params.get("utm_source") ?? undefined,
    medium: params.get("utm_medium") ?? undefined,
    campaign: params.get("utm_campaign") ?? undefined,
    term: params.get("utm_term") ?? undefined,
    content: params.get("utm_content") ?? undefined,
    landingPath: window.location.pathname + window.location.search,
    referer: document.referrer || undefined,
  };
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utm));
  } catch {
    // ignore
  }
  return utm;
}

// ── Ingest ──────────────────────────────────────────────────────────────

interface TrackPayload {
  eventType:
    | "pageview"
    | "click"
    | "scroll_depth"
    | "add_to_cart"
    | "checkout_start"
    | "purchase"
    | "exit_intent"
    | "search"
    | "form_submit"
    | "form_focus"
    | "copy"
    | "visibility"
    | "engagement"
    | "perf"
    | "outbound_click"
    | "video_play";
  path?: string;
  title?: string;
  metadata?: Record<string, unknown>;
}

/**
 * Snapshot único de "fingerprint" leve do browser — enviado em todos os
 * eventos dentro de `metadata.client`. Zero custo adicional de PII (todos
 * dados já expostos via navigator/screen/performance).
 */
function clientSnapshot(): Record<string, unknown> {
  const nav = navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
    deviceMemory?: number;
  };
  const conn = nav.connection;
  return {
    dpr: window.devicePixelRatio,
    viewport: { w: window.innerWidth, h: window.innerHeight },
    touch: "ontouchstart" in window,
    cores: navigator.hardwareConcurrency,
    memoryGb: nav.deviceMemory,
    online: navigator.onLine,
    platform: (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData
      ?.platform ?? navigator.platform,
    colorScheme: window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light",
    reducedMotion: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
    connection: conn
      ? {
          type: conn.effectiveType,
          downlinkMbps: conn.downlink,
          rttMs: conn.rtt,
          saveData: conn.saveData,
        }
      : undefined,
    cookieEnabled: navigator.cookieEnabled,
  };
}

function send(payload: TrackPayload): void {
  // Banner de cookies permanece visível (aviso ao usuário), mas a captura
  // acontece antes do consent pra não perder visitas orgânicas que ignoram
  // o banner. Bot/crawler continua filtrado no backend pelo UA parser.
  const utm = captureOrLoadUtm();
  const body = JSON.stringify({
    visitorKey: getOrCreateVisitorKey(),
    sessionKey: getOrCreateSessionKey(),
    eventType: payload.eventType,
    path: payload.path ?? window.location.pathname,
    title: payload.title ?? document.title,
    referer: utm.referer,
    landingPath: utm.landingPath,
    utm: {
      source: utm.source,
      medium: utm.medium,
      campaign: utm.campaign,
      term: utm.term,
      content: utm.content,
    },
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenWidth: window.screen.width,
    screenHeight: window.screen.height,
    metadata: {
      ...(payload.metadata ?? {}),
      client: clientSnapshot(),
      ts: Date.now(),
    },
  });

  try {
    // Beacon is best for reliability (especially on unload events) but some
    // requests need fetch to keep the connection alive.
    if (typeof navigator.sendBeacon === "function") {
      const blob = new Blob([body], { type: "application/json" });
      const ok = navigator.sendBeacon(ENDPOINT, blob);
      if (ok) return;
    }
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
      credentials: "omit",
    }).catch(() => undefined);
  } catch {
    // Swallow — tracking is best-effort
  }
}

// ── Public API ──────────────────────────────────────────────────────────

export function trackPageview(path?: string, title?: string): void {
  send({ eventType: "pageview", path, title });
}

export function trackEvent(
  eventType: TrackPayload["eventType"],
  metadata: Record<string, unknown> = {}
): void {
  send({ eventType, metadata });
}

/**
 * Installs automatic pageview tracking tied to History API changes and
 * react-router navigation. Also wires up scroll-depth + exit-intent hooks.
 */
export function installAnalytics(): void {
  if (typeof window === "undefined") return;

  // Listen for consent changes from the cookie banner — when user accepts,
  // fire the deferred initial pageview right away so we don't lose the
  // landing hit.
  window.addEventListener("bzv:consent-change", (e) => {
    const choice = (e as CustomEvent<string>).detail;
    if (choice === "accepted") {
      trackPageview();
    }
  });

  // Initial pageview (only fires if consent was already given)
  trackPageview();

  // Patch history methods to detect SPA navigation
  const push = history.pushState;
  const replace = history.replaceState;
  history.pushState = function (...args: Parameters<typeof history.pushState>) {
    const result = push.apply(this, args);
    setTimeout(() => trackPageview(), 0);
    return result;
  };
  history.replaceState = function (...args: Parameters<typeof history.replaceState>) {
    const result = replace.apply(this, args);
    setTimeout(() => trackPageview(), 0);
    return result;
  };
  window.addEventListener("popstate", () => trackPageview());

  // Scroll-depth: fire once per page at 25/50/75/100
  let scrollFired = new Set<number>();
  let lastPath = window.location.pathname;
  window.addEventListener(
    "scroll",
    () => {
      if (window.location.pathname !== lastPath) {
        scrollFired = new Set();
        lastPath = window.location.pathname;
      }
      const scrolled =
        (window.scrollY + window.innerHeight) /
        Math.max(document.documentElement.scrollHeight, 1);
      const pct = Math.floor(scrolled * 100);
      for (const threshold of [25, 50, 75, 100]) {
        if (pct >= threshold && !scrollFired.has(threshold)) {
          scrollFired.add(threshold);
          trackEvent("scroll_depth", { threshold });
        }
      }
    },
    { passive: true }
  );

  // Exit intent (desktop only, mouse leaving via top edge)
  document.addEventListener("mouseleave", (e) => {
    if (e.clientY <= 0) {
      trackEvent("exit_intent", { path: window.location.pathname });
    }
  });

  // ── Click tracker (capture anchor/button selector + text) ────────────
  document.addEventListener(
    "click",
    (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const el = t.closest("a,button,[role='button'],[data-track]") as HTMLElement | null;
      if (!el) return;
      const tag = el.tagName.toLowerCase();
      const text = (el.textContent || "").trim().slice(0, 120);
      const href = el.getAttribute("href");
      const id = el.id || undefined;
      const cls = el.className?.toString().slice(0, 160);
      const dataTrack = el.getAttribute("data-track") || undefined;
      const isOutbound =
        !!href && /^https?:\/\//i.test(href) && !href.includes(window.location.host);
      trackEvent(isOutbound ? "outbound_click" : "click", {
        tag,
        text,
        href,
        id,
        cls,
        dataTrack,
        path: window.location.pathname,
      });
    },
    { capture: true, passive: true }
  );

  // ── Form submit + first focus ────────────────────────────────────────
  document.addEventListener(
    "submit",
    (e) => {
      const f = e.target as HTMLFormElement | null;
      if (!f || f.tagName !== "FORM") return;
      const inputs = Array.from(f.querySelectorAll("input,select,textarea"))
        .map((el) => (el as HTMLInputElement).name || (el as HTMLInputElement).type)
        .filter(Boolean)
        .slice(0, 20);
      trackEvent("form_submit", {
        id: f.id || undefined,
        name: f.getAttribute("name") || undefined,
        action: f.action || undefined,
        fields: inputs,
        path: window.location.pathname,
      });
    },
    { capture: true, passive: true }
  );

  const focusedForms = new WeakSet<HTMLFormElement>();
  document.addEventListener(
    "focusin",
    (e) => {
      const t = e.target as HTMLElement | null;
      const f = t?.closest("form") as HTMLFormElement | null;
      if (!f || focusedForms.has(f)) return;
      focusedForms.add(f);
      trackEvent("form_focus", {
        id: f.id || undefined,
        name: f.getAttribute("name") || undefined,
        firstField: (t as HTMLInputElement).name || (t as HTMLInputElement).type,
        path: window.location.pathname,
      });
    },
    { capture: true, passive: true }
  );

  // ── Copy events (conteúdo sendo copiado é sinal forte de interesse) ──
  document.addEventListener("copy", () => {
    const sel = (document.getSelection()?.toString() || "").trim();
    if (!sel) return;
    trackEvent("copy", {
      length: sel.length,
      preview: sel.slice(0, 80),
      path: window.location.pathname,
    });
  });

  // ── Visibility + engagement time (ms de aba em foco) ─────────────────
  let visibleSince = Date.now();
  let engagedMs = 0;
  let lastFlushAt = Date.now();
  const FLUSH_INTERVAL_MS = 30_000;

  function flushEngagement(reason: string) {
    const now = Date.now();
    if (document.visibilityState === "visible") {
      engagedMs += now - visibleSince;
      visibleSince = now;
    }
    if (engagedMs < 1500) return;
    trackEvent("engagement", {
      activeMs: engagedMs,
      reason,
      path: window.location.pathname,
    });
    engagedMs = 0;
    lastFlushAt = now;
  }

  document.addEventListener("visibilitychange", () => {
    const now = Date.now();
    if (document.visibilityState === "hidden") {
      engagedMs += now - visibleSince;
      trackEvent("visibility", { state: "hidden", activeMs: engagedMs });
    } else {
      visibleSince = now;
      trackEvent("visibility", { state: "visible" });
    }
  });

  window.addEventListener("pagehide", () => flushEngagement("pagehide"));
  window.addEventListener("beforeunload", () => flushEngagement("beforeunload"));

  setInterval(() => {
    if (Date.now() - lastFlushAt >= FLUSH_INTERVAL_MS) flushEngagement("interval");
  }, FLUSH_INTERVAL_MS);

  // ── Performance timings (Core Web Vitals simplificado) ───────────────
  try {
    // PerformanceObserver pra LCP + FCP
    const perfData: Record<string, number> = {};
    const poLcp = new PerformanceObserver((entries) => {
      const last = entries.getEntries().slice(-1)[0];
      if (last) perfData.lcp = Math.round(last.startTime);
    });
    poLcp.observe({ type: "largest-contentful-paint", buffered: true });

    const poFcp = new PerformanceObserver((entries) => {
      const fcp = entries.getEntries().find((e) => e.name === "first-contentful-paint");
      if (fcp) perfData.fcp = Math.round(fcp.startTime);
    });
    poFcp.observe({ type: "paint", buffered: true });

    // TTFB via Navigation Timing
    setTimeout(() => {
      const nav = performance.getEntriesByType("navigation")[0] as
        | PerformanceNavigationTiming
        | undefined;
      if (nav) {
        perfData.ttfb = Math.round(nav.responseStart);
        perfData.dcl = Math.round(nav.domContentLoadedEventEnd);
        perfData.loadDone = Math.round(nav.loadEventEnd);
      }
      if (Object.keys(perfData).length) {
        trackEvent("perf", { ...perfData, path: window.location.pathname });
      }
    }, 4000);
  } catch {
    // PerformanceObserver indisponível — ignora
  }

  // ── Vídeo (play event em <video> tags) ───────────────────────────────
  document.addEventListener(
    "play",
    (e) => {
      const v = e.target as HTMLVideoElement;
      if (v.tagName !== "VIDEO") return;
      trackEvent("video_play", {
        src: v.currentSrc?.slice(0, 200),
        duration: Math.round(v.duration),
        path: window.location.pathname,
      });
    },
    { capture: true, passive: true }
  );
}
